import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskLines, Reveal } from "../components/Reveal";
import RibbonMarquee from "../components/RibbonMarquee";
import IntroVideo from "../components/IntroVideo";
import { NETWORK, SUMMIT, MANIFESTO, VIDEO_URL, IMAGES } from "../data/content";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [news, setNews] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/announcements`)
      .then((r) => setNews(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <IntroVideo />
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        <motion.div style={{ scale, y }} className="absolute inset-0">
          <video
            className="w-full h-full object-cover"
            src={VIDEO_URL}
            poster={IMAGES.peak}
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-background/40" />
        </motion.div>

        <motion.div
          style={{ opacity }}
          className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 1 }}
            className="overline text-secondary mb-6"
          >
            {NETWORK.short} — Est. {NETWORK.established}
          </motion.p>

          <h1 className="font-display uppercase text-foreground text-6xl sm:text-8xl lg:text-[11vw] leading-[0.85]">
            <MaskLines lines={["Consecrate", "For War"]} start={0.25} />
          </h1>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mt-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="font-editorial italic text-2xl md:text-3xl text-white/80 max-w-lg"
            >
              “{NETWORK.scripture}”
              <span className="block not-italic overline text-secondary text-sm mt-3">
                {NETWORK.scriptureRef}
              </span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9 }}
              className="flex items-center gap-4"
            >
              <Link
                to="/summit"
                data-testid="hero-register-btn"
                className="group bg-primary text-primary-foreground overline px-8 py-5 flex items-center gap-3 hover:bg-primary/85 transition-colors duration-300"
              >
                Register for Summit
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
              </Link>
              <Link
                to="/leadership"
                className="border border-secondary text-secondary overline px-8 py-5 hover:bg-secondary hover:text-secondary-foreground transition-colors duration-300"
              >
                Leadership
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 overline text-white/40 text-[0.6rem]"
        >
          Scroll to Ascend
        </motion.div>
      </section>

      <RibbonMarquee />

      {/* MANIFESTO */}
      <section className="py-28 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto" data-testid="manifesto">
        <Reveal className="mb-20">
          <p className="overline text-primary mb-4">The Manifesto</p>
          <h2 className="font-editorial text-4xl md:text-6xl text-foreground max-w-3xl leading-tight">
            A covenant family called to dwell in the high places of God.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {MANIFESTO.map((m, i) => (
            <Reveal
              key={m.num}
              delay={i * 0.12}
              className="bg-background p-8 md:p-12 group hover:bg-muted/40 transition-colors duration-500"
            >
              <span className="font-display text-secondary/40 text-6xl group-hover:text-secondary transition-colors duration-500">
                {m.num}
              </span>
              <h3 className="font-display uppercase text-2xl text-foreground mt-6 mb-4">{m.title}</h3>
              <p className="font-body text-muted-foreground leading-relaxed">{m.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SUMMIT TEASER */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="summit-teaser">
        <div className="absolute inset-0">
          <img src={IMAGES.peak} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/80" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-secondary mb-6">{SUMMIT.dates}</p>
            <h2 className="font-display uppercase text-foreground text-5xl md:text-8xl leading-[0.9]">
              PeakDwellers<br />Summit 2026
            </h2>
            <p className="font-body text-muted-foreground max-w-xl mt-8 leading-relaxed">
              {SUMMIT.purpose}
            </p>
            <Link
              to="/summit"
              data-testid="summit-teaser-link"
              className="inline-flex items-center gap-3 mt-10 group overline text-foreground"
            >
              <span className="border-b border-primary pb-1">View Full Program</span>
              <ArrowRight size={18} className="text-primary group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
            <a
              href={SUMMIT.programBook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-program-book"
              className="inline-flex items-center gap-3 mt-5 overline text-secondary hover:text-foreground transition-colors"
            >
              <span className="border-b border-secondary pb-1">Download the 2026 Program Book (PDF)</span>
            </a>
          </Reveal>
        </div>
      </section>

      {/* NEWS & ANNOUNCEMENTS */}
      {news.length > 0 && (
        <section className="py-28 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto" data-testid="home-news">
          <Reveal className="mb-16 flex items-end justify-between flex-wrap gap-6">
            <div>
              <p className="overline text-primary mb-4">Latest Word</p>
              <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground">News & Announcements</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            {news.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.1} className="bg-background p-8 md:p-10">
                {n.pinned && <span className="overline text-primary text-[0.55rem]">Pinned</span>}
                <p className="overline text-secondary text-[0.55rem] mt-1">{n.audience}</p>
                <h3 className="font-display uppercase text-2xl text-foreground mt-4 mb-3">{n.title}</h3>
                <p className="font-body text-muted-foreground leading-relaxed whitespace-pre-line">{n.body}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* MISSION STRIP */}
      <section className="py-28 md:py-40 px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <p className="overline text-primary">Our Mission</p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-8">
            <p className="font-editorial text-3xl md:text-5xl text-foreground leading-tight">
              {NETWORK.mission}
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
